/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useMemo, useRef, useState } from 'react';
import { StyleSheet, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { spacing } from '../../shared/theme/spacing'
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors';
import { posSharedUi as posUi } from '../../shared/ui/posSharedUi';
import { OperationalFilterSearchField } from '../../shared/filters/OperationalFilterPrimitives'
import { useOperationalFilterLayout } from '../../shared/filters/useOperationalFilterLayout';
import { useOperationalWebSearchFocus } from '../../shared/ui/hooks-screen/useOperationalWebSearchFocus';
import { PaperSheet } from '../../shared/ticket/PaperSheet'
import { TicketKpiStrip } from '../../shared/ticket/TicketKpiStrip'
import type { TicketKpiItem } from '../../shared/ticket/TicketKpiStrip'
import { TicketModuleHeader } from '../../shared/ticket/TicketModuleHeader'
import { TicketMoreActionsSheet } from '../../shared/ticket/TicketMoreActionsSheet';

type Props = {
  activeCount: number;
  expiredCount: number;
  programmedCount: number;
  totalCount: number;
  loadError: string | null;
  showReadonlyBanner?: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  canEdit: boolean;
  canExport?: boolean;
  canEnterBatch?: boolean;
  onCreate: () => void;
  onExportCsv: () => void;
  onEnterBatchMode?: () => void;
  /** PC: botón Filtros desk en la toolbar. */
  filterTrigger?: React.ReactNode;
  /** Cero promos sin filtros: oculta el KPI strip en 0 (ruido en vacío). */
  hideMetrics?: boolean;
};

type HeaderSubProps = Props & { searchInputRef: React.RefObject<TextInput | null> };

export function PromotionsScreenHeader(props: Props) {
  const { layoutWide } = useOperationalFilterLayout();
  const searchInputRef = useRef<TextInput>(null);
  useOperationalWebSearchFocus(searchInputRef, { shortcutsEnabled: true });
  const subProps: HeaderSubProps = { ...props, searchInputRef };
  if (layoutWide) return <WideHeader {...subProps} />;
  return <MobileHeader {...subProps} />;
}

function usePromotionKpiItems(
  activeCount: number,
  expiredCount: number,
  programmedCount: number,
  totalCount: number
): TicketKpiItem[] {
  return useMemo(
    () => [
      { label: 'Activas', value: String(activeCount), tone: activeCount > 0 ? ('ok' as const) : ('default' as const), testID: 'promotions.kpi.active' },
      { label: 'Programadas', value: String(programmedCount), tone: programmedCount > 0 ? ('warn' as const) : ('default' as const), testID: 'promotions.kpi.scheduled' },
      { label: 'Vencidas', value: String(expiredCount), tone: expiredCount > 0 ? ('warn' as const) : ('default' as const), testID: 'promotions.kpi.expired' },
      { label: 'Total', value: String(totalCount), testID: 'promotions.kpi.total' },
    ],
    [activeCount, expiredCount, programmedCount, totalCount]
  );
}

function WideHeader({
  activeCount,
  expiredCount,
  programmedCount,
  totalCount,
  loadError,
  showReadonlyBanner = false,
  search,
  onSearchChange,
  canEdit,
  canExport = true,
  canEnterBatch = true,
  onCreate,
  onExportCsv,
  onEnterBatchMode,
  filterTrigger,
  hideMetrics = false,
  searchInputRef,
}: HeaderSubProps) {
  const kpiItems = usePromotionKpiItems(activeCount, expiredCount, programmedCount, totalCount);
  const [moreActionsOpen, setMoreActionsOpen] = useState(false);

  const sheetActions = useMemo(() => {
    const items: Array<{ key: string; label: string; hint: string; icon: React.ComponentProps<typeof Ionicons>['name']; onPress: () => void }> = [];
    if (canExport) items.push({ key: 'export-csv', label: 'Exportar CSV', hint: 'Bajar el listado de promos', icon: 'download-outline', onPress: onExportCsv });
    if (canEdit && canEnterBatch && onEnterBatchMode) items.push({ key: 'batch-select', label: 'Seleccionar varias', hint: 'Activar o desactivar varias', icon: 'checkbox-outline', onPress: onEnterBatchMode });
    return items;
  }, [canEdit, canEnterBatch, canExport, onEnterBatchMode, onExportCsv]);

  return (
    <View style={styles.headerStack}>
      <TicketModuleHeader
        kicker="Promociones"
        title="Tus promociones"
        meta="Armá descuentos, 2×1 y códigos. Se aplican solos en el POS."
        testID="promotions.header.sheet"
        // Cero promos: el CTA vive en el empty state — no duplicar «Nueva promoción».
        trailing={canEdit && totalCount > 0 ? (
          <TouchableOpacity style={[styles.deskBtnGold, !canEdit && posUi.disabledCard]} onPress={onCreate} disabled={!canEdit} accessibilityRole="button" accessibilityLabel="Nueva promoción" testID="promociones.new">
            <Text style={styles.deskBtnGoldText}>Nueva promoción</Text>
          </TouchableOpacity>
        ) : null}
        tools={
          <View style={styles.deskToolsRow}>
            <View style={styles.deskSearchFlex}>
              <OperationalFilterSearchField inputRef={searchInputRef} value={search} onChangeText={onSearchChange} placeholder="Buscar promoción…" autoCapitalize="none" appearance="desk" />
            </View>
            {filterTrigger}
            {sheetActions.length > 0 ? (
              <TouchableOpacity style={styles.deskBtn} onPress={() => setMoreActionsOpen(true)} accessibilityRole="button" accessibilityLabel="Más acciones" testID="promotions.more.open">
                <Text style={styles.deskBtnText}>Más</Text>
              </TouchableOpacity>
            ) : null}
          </View>
        }
      >
        <TicketMoreActionsSheet visible={moreActionsOpen} title="Más" actions={sheetActions} onClose={() => setMoreActionsOpen(false)} />
      </TicketModuleHeader>

      {!hideMetrics ? (
        <PaperSheet testID="promotions.kpi.sheet">
          <TicketKpiStrip items={kpiItems} layout="desk" variant="soft" />
        </PaperSheet>
      ) : null}

      {showReadonlyBanner ? (
        <View style={posUi.readOnlyBanner}><Text style={posUi.readOnlyText}>Solo lectura. Podés mirar las promos. No crear ni editar.</Text></View>
      ) : null}
      {loadError ? (
        <View style={styles.errorBanner}><Text style={styles.errorText}>{loadError}</Text></View>
      ) : null}
    </View>
  );
}

function MobileHeader({
  activeCount,
  expiredCount,
  programmedCount,
  totalCount,
  loadError,
  showReadonlyBanner = false,
  search,
  onSearchChange,
  canEdit,
  canExport = true,
  canEnterBatch = true,
  onCreate,
  onExportCsv,
  onEnterBatchMode,
  filterTrigger,
  hideMetrics = false,
  searchInputRef,
}: HeaderSubProps) {
  const [moreActionsOpen, setMoreActionsOpen] = useState(false);
  const kpiItems = usePromotionKpiItems(activeCount, expiredCount, programmedCount, totalCount);

  const sheetActions = useMemo(() => {
    const items: Array<{ key: string; label: string; hint: string; icon: React.ComponentProps<typeof Ionicons>['name']; onPress: () => void }> = [];
    if (canExport) items.push({ key: 'export-csv', label: 'Exportar CSV', hint: 'Bajar el listado de promos', icon: 'download-outline', onPress: onExportCsv });
    if (canEdit && canEnterBatch && onEnterBatchMode) items.push({ key: 'batch-select', label: 'Seleccionar varias', hint: 'Activar o desactivar varias', icon: 'checkbox-outline', onPress: onEnterBatchMode });
    return items;
  }, [canEdit, canEnterBatch, canExport, onEnterBatchMode, onExportCsv]);

  return (
    <View style={styles.headerStack}>
      <TicketModuleHeader
        kicker="Promociones"
        title="Tus promociones"
        meta="Armá descuentos, 2×1 y códigos. Se aplican solos en el POS."
        testID="promotions.header.sheet"
        tools={
          <>
            <View style={styles.searchRowMobile}>
              <View style={styles.searchBoxMobile}>
                <Ionicons name="search" size={16} color={ticketColors.ink3} />
                <TextInput ref={searchInputRef} value={search} onChangeText={onSearchChange} placeholder="Buscar promoción…" placeholderTextColor={ticketColors.ink3} style={styles.searchInputMobile} accessibilityLabel="Buscar promociones" autoCorrect={false} returnKeyType="search" blurOnSubmit={false} />
                {search.trim().length > 0 ? (
                  <TouchableOpacity onPress={() => onSearchChange('')} accessibilityRole="button" accessibilityLabel="Limpiar búsqueda">
                    <Ionicons name="close-circle" size={18} color={ticketColors.ink2} />
                  </TouchableOpacity>
                ) : null}
              </View>
            </View>
            <View style={styles.toolsRowMobile}>
              {filterTrigger}
              {sheetActions.length > 0 ? (
                <TouchableOpacity style={styles.actionMoreMobile} onPress={() => setMoreActionsOpen(true)} activeOpacity={0.6} accessibilityRole="button" accessibilityLabel="Más opciones de promociones" accessibilityHint="Exportar CSV, seleccionar varias" testID="promotions.more.open">
                  <Text style={styles.actionMoreTextMobile}>Más</Text>
                </TouchableOpacity>
              ) : null}
            </View>
            {canEdit && totalCount > 0 ? (
              <TouchableOpacity style={[styles.actionPrimaryMobile, !canEdit && posUi.disabledCard]} onPress={onCreate} disabled={!canEdit} activeOpacity={0.7} accessibilityRole="button" accessibilityLabel="Nueva promoción" accessibilityState={{ disabled: !canEdit }} testID="promociones.new">
                <Text style={styles.actionPrimaryTextMobile}>Nueva promoción</Text>
              </TouchableOpacity>
            ) : null}
          </>
        }
      />

      {!hideMetrics ? (
        <PaperSheet testID="promotions.kpi.sheet">
          <TicketKpiStrip items={kpiItems} layout="mobile" variant="soft" />
        </PaperSheet>
      ) : null}

      <TicketMoreActionsSheet visible={moreActionsOpen} title="Más opciones" actions={sheetActions} onClose={() => setMoreActionsOpen(false)} />

      {showReadonlyBanner ? (
        <View style={posUi.readOnlyBanner}><Text style={posUi.readOnlyText}>Solo lectura. Podés mirar las promos. No crear ni editar.</Text></View>
      ) : null}
      {loadError ? (
        <View style={styles.errorBanner}><Text style={styles.errorText}>{loadError}</Text></View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  headerStack: { gap: 10 },
  deskToolsRow: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 8 },
  deskSearchFlex: { flex: 1, minWidth: 180 },
  deskBtn: { height: 42, borderRadius: 10, borderWidth: StyleSheet.hairlineWidth, borderColor: ticketColors.ruleSoft, backgroundColor: ticketColors.tile, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 14 },
  deskBtnText: { fontSize: 14, fontWeight: '700', color: ticketColors.ink2, fontFamily: ticketFonts.sans },
  deskBtnGold: { height: 42, borderRadius: 10, backgroundColor: ticketColors.gold, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 14 },
  deskBtnGoldText: { fontSize: 14, fontWeight: '800', color: ticketColors.goldInk, fontFamily: ticketFonts.sans },
  searchRowMobile: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  searchBoxMobile: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 8, borderWidth: StyleSheet.hairlineWidth, borderColor: ticketColors.ruleSoft, backgroundColor: ticketColors.tile, borderRadius: 10, paddingHorizontal: 12, height: 44 },
  searchInputMobile: { flex: 1, fontSize: 15, color: ticketColors.ink, fontFamily: ticketFonts.sans, padding: 0 },
  toolsRowMobile: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  actionPrimaryMobile: { height: 48, borderRadius: 10, backgroundColor: ticketColors.gold, alignItems: 'center', justifyContent: 'center' },
  actionPrimaryTextMobile: { fontSize: 15, fontWeight: '800', color: ticketColors.goldInk, fontFamily: ticketFonts.sans },
  actionMoreMobile: { flex: 1, height: 44, borderRadius: 10, borderWidth: StyleSheet.hairlineWidth, borderColor: ticketColors.ruleSoft, backgroundColor: ticketColors.tile, alignItems: 'center', justifyContent: 'center' },
  actionMoreTextMobile: { fontSize: 14, fontWeight: '700', color: ticketColors.ink, fontFamily: ticketFonts.sans },
  errorBanner: { padding: spacing.md, borderRadius: 10, borderWidth: 1, borderColor: ticketColors.gold, backgroundColor: ticketColors.paper },
  errorText: { color: ticketColors.gold, lineHeight: 20, fontWeight: '600', fontFamily: ticketFonts.sans },
});
